export const FINANCIAL_INSIGHTS_LABEL = 'Financial Insights'
export const FINANCIAL_INSIGHTS_TOOLTIP =
  'Enable Financial Insights powered by CreditSense. Renters can verify their financial position by securely connecting their bank account.'

export const SMART_DOCS_FIELDS_SECTION_LABEL = 'SmartDocs Fields'

export const SMART_DOCS_FIELD_SETTING = {
  ENABLED: 'enabled',
  IMPORTANCE: 'importance',
  SENSITIVITY: 'sensitivity',
}

export const buildSmartDocsFieldAttributeKey = (fieldKey, setting) =>
  `smart_docs.${fieldKey}.${setting}`

export const SMART_DOCS_IMPORTANCE_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
]

export const SMART_DOCS_SENSITIVITY_OPTIONS = [
  { label: 'Exact', value: 'exact' },
  { label: 'Partial', value: 'partial' },
  { label: 'Fuzzy', value: 'fuzzy' },
]

export const SMART_DOCS_FIELD_DEFAULTS = {
  enabled: true,
  importance: 'medium',
  sensitivity: 'partial',
}

/**
 * Rule keys for `disabledWhen` on SmartDocs field definitions.
 * `disabledWhen` is an array of these keys; **every** rule must match for the field to be disabled.
 */
export const SMART_DOCS_DISABLED_WHEN = {
  /** Payslip SmartDocs analysis is off */
  PAYSLIP_ANALYSIS_OFF: 'payslip_analysis_off',
  /** Rental ledger SmartDocs analysis is off */
  RENTAL_LEDGER_ANALYSIS_OFF: 'rental_ledger_analysis_off',
}

const SMART_DOCS_DISABLED_WHEN_EVALUATORS = {
  [SMART_DOCS_DISABLED_WHEN.PAYSLIP_ANALYSIS_OFF]: (context) =>
    !context.smartDocsPayslipEnabled,
  [SMART_DOCS_DISABLED_WHEN.RENTAL_LEDGER_ANALYSIS_OFF]: (context) =>
    !context.smartDocsRentalLedgerEnabled,
}

export const SMART_DOCS_FIELDS = [
  {
    fieldKey: 'payslips.income',
    label: 'Income',
    tooltip:
      'Turn on SmartDocs verification of income on payslips, then set importance and sensitivity.',
    disabledWhen: [SMART_DOCS_DISABLED_WHEN.PAYSLIP_ANALYSIS_OFF],
  },
  {
    fieldKey: 'payslips.employer_name',
    label: 'Employer Name',
    tooltip:
      'Turn on SmartDocs verification of employer names on payslips, then set importance and sensitivity.',
    disabledWhen: [SMART_DOCS_DISABLED_WHEN.PAYSLIP_ANALYSIS_OFF],
  },
  {
    fieldKey: 'issue_date',
    label: 'Issue date',
    tooltip:
      'Turn on SmartDocs verification of payslip issue dates, then set importance and sensitivity.',
    disabledWhen: [
      SMART_DOCS_DISABLED_WHEN.PAYSLIP_ANALYSIS_OFF,
      SMART_DOCS_DISABLED_WHEN.RENTAL_LEDGER_ANALYSIS_OFF,
    ],
  },
]

/**
 * @param {object} field - entry from SMART_DOCS_FIELDS
 * @param {object} [context]
 * @param {boolean} [context.smartDocsPayslipEnabled]
 * @param {boolean} [context.smartDocsRentalLedgerEnabled]
 * @param {boolean} [context.isUserAdmin]
 * @param {boolean} [context.isActionDisabled]
 * @returns {boolean} true when the field should be treated as prerequisite-disabled (UI + effective enabled false)
 */
export const isSmartDocsFieldRuleDisabled = (field, context = {}) => {
  const when = field.disabledWhen
  if (!Array.isArray(when) || when.length === 0) return false
  return when.every((ruleKey) => {
    const fn = SMART_DOCS_DISABLED_WHEN_EVALUATORS[ruleKey]
    return typeof fn === 'function' && fn(context)
  })
}

/**
 * Merge stored values with defaults and force `enabled: false` for any field
 * that is rule-disabled under `context`. Used for save payloads and state normalization.
 */
export const getSmartDocsFieldValuesForSerialization = (
  smartDocsFieldValues = {},
  context,
  fields = SMART_DOCS_FIELDS,
) =>
  fields.reduce((acc, field) => {
    const key = field.fieldKey
    const merged = {
      ...SMART_DOCS_FIELD_DEFAULTS,
      ...(smartDocsFieldValues[key] || {}),
    }
    acc[key] = isSmartDocsFieldRuleDisabled(field, context)
      ? { ...merged, enabled: false }
      : merged
    return acc
  }, {})

const SMART_DOCS_SETTINGS = Object.values(SMART_DOCS_FIELD_SETTING)

export const getDefaultSmartDocsFieldValues = (fields = SMART_DOCS_FIELDS) =>
  fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.fieldKey]: {
        ...SMART_DOCS_FIELD_DEFAULTS,
      },
    }),
    {},
  )

export const parseSmartDocsFieldValues = (
  attributesData = {},
  fields = SMART_DOCS_FIELDS,
) =>
  fields.reduce((acc, field) => {
    const values = SMART_DOCS_SETTINGS.reduce((fieldValues, setting) => {
      const attributeKey = buildSmartDocsFieldAttributeKey(
        field.fieldKey,
        setting,
      )
      const rawValue = attributesData[attributeKey]

      if (setting === SMART_DOCS_FIELD_SETTING.ENABLED) {
        return {
          ...fieldValues,
          [setting]:
            rawValue === undefined
              ? SMART_DOCS_FIELD_DEFAULTS[setting]
              : String(rawValue) === 'true',
        }
      }

      return {
        ...fieldValues,
        [setting]: rawValue ?? SMART_DOCS_FIELD_DEFAULTS[setting],
      }
    }, {})

    return {
      ...acc,
      [field.fieldKey]: values,
    }
  }, {})

export const serializeSmartDocsFieldValues = (
  smartDocsFieldValues = {},
  fields = SMART_DOCS_FIELDS,
) =>
  fields.reduce((acc, field) => {
    const values = {
      ...SMART_DOCS_FIELD_DEFAULTS,
      ...(smartDocsFieldValues[field.fieldKey] || {}),
    }

    return {
      ...acc,
      [buildSmartDocsFieldAttributeKey(
        field.fieldKey,
        SMART_DOCS_FIELD_SETTING.ENABLED,
      )]: Boolean(values.enabled).toString(),
      [buildSmartDocsFieldAttributeKey(
        field.fieldKey,
        SMART_DOCS_FIELD_SETTING.IMPORTANCE,
      )]: values.importance,
      [buildSmartDocsFieldAttributeKey(
        field.fieldKey,
        SMART_DOCS_FIELD_SETTING.SENSITIVITY,
      )]: values.sensitivity,
    }
  }, {})
